import React, { useState } from 'react';
import { Search, Loader2, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { roleOverrideService } from '@/services/roleOverrideService';
import type { RateResolutionResponse } from '@/types/roleOverride';
import { formatCurrency, formatPercentage } from '@/utils/formatters';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Mock data for dropdowns - in production, these would come from API calls
const mockEmployees = [
  { id: 1, name: 'John Smith', role: 'Senior Developer' },
  { id: 2, name: 'Sarah Johnson', role: 'Senior Developer' },
  { id: 3, name: 'Mike Chen', role: 'Junior Developer' },
  { id: 4, name: 'Emily Davis', role: 'Project Manager' },
];

const mockProjects = [
  { id: 1, name: 'ABC Corp Website Redesign' },
  { id: 2, name: 'XYZ Mobile App' },
  { id: 3, name: 'Internal Tools Development' },
  { id: 4, name: 'E-commerce Platform' },
];

const mockRoles = [
  { id: 1, name: 'Senior Developer' },
  { id: 2, name: 'Junior Developer' },
  { id: 3, name: 'Project Manager' },
];

export const RateResolutionDemo: React.FC = () => {
  const [employeeId, setEmployeeId] = useState<string>('1');
  const [roleId, setRoleId] = useState<string>('1');
  const [projectId, setProjectId] = useState<string>('1');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [isResolving, setIsResolving] = useState(false);
  const [result, setResult] = useState<RateResolutionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleResolve = async () => {
    setIsResolving(true);
    setError(null);
    setResult(null);

    try {
      const response = await roleOverrideService.resolveRate({
        employeeId: parseInt(employeeId),
        roleId: parseInt(roleId),
        projectId: parseInt(projectId),
        effectiveDate: effectiveDate + 'T00:00:00Z',
      });
      setResult(response);
      toast.success('Rate resolved successfully');
    } catch (err) {
      console.error('Error resolving rate:', err);
      setError('Failed to resolve rate');
      toast.error('Failed to resolve rate');
    } finally {
      setIsResolving(false);
    }
  };

  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'EmployeeProject':
        return <Badge className="bg-purple-500">Employee + Project</Badge>;
      case 'EmployeeGlobal':
        return <Badge className="bg-blue-500">Employee Global</Badge>;
      case 'ProjectRole':
        return <Badge className="bg-green-500">Project + Role</Badge>;
      case 'BaseRole':
        return <Badge variant="secondary">Base Role</Badge>;
      default:
        return <Badge variant="outline">{source}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Rate Resolution Demo</h3>
        <p className="text-sm text-muted-foreground">
          Test the rate resolution algorithm to see which rate applies
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Input Parameters</CardTitle>
          <CardDescription>Enter the details to resolve the effective rate</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="employeeId">Employee</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger id="employeeId">
                  <SelectValue placeholder="Select an employee" />
                </SelectTrigger>
                <SelectContent>
                  {mockEmployees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id.toString()}>
                      {employee.name} ({employee.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="roleId">Role</Label>
              <Select value={roleId} onValueChange={setRoleId}>
                <SelectTrigger id="roleId">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {mockRoles.map((role) => (
                    <SelectItem key={role.id} value={role.id.toString()}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="projectId">Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger id="projectId">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {mockProjects.map((project) => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="effectiveDate">Effective Date</Label>
              <Input
                id="effectiveDate"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </div>
          </div>

          <Button onClick={handleResolve} disabled={isResolving} className="w-full">
            {isResolving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Resolving...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Resolve Rate
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {result && (
        <div className="space-y-4">
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <CardTitle className="text-green-900">Effective Rate Found</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Source:</span>
                {getSourceBadge(result.effectiveRate.source)}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Billing Rate</div>
                  <div className="text-2xl font-bold">
                    {result.effectiveRate.billingRate
                      ? formatCurrency(result.effectiveRate.billingRate, result.effectiveRate.currency)
                      : '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Cost Rate</div>
                  <div className="text-2xl font-bold">
                    {result.effectiveRate.costRate
                      ? formatCurrency(result.effectiveRate.costRate, result.effectiveRate.currency)
                      : '-'}
                  </div>
                </div>
              </div>

              {result.effectiveRate.marginPercentage && (
                <div>
                  <div className="text-sm text-muted-foreground">Margin</div>
                  <div className="text-xl font-semibold">
                    {formatPercentage(result.effectiveRate.marginPercentage)}
                  </div>
                </div>
              )}

              <div>
                <div className="text-sm text-muted-foreground">Source Name</div>
                <div className="font-medium">{result.effectiveRate.sourceName}</div>
              </div>

              {result.effectiveRate.isOverridden && (
                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                  ⚠️ Rate is overridden
                </Badge>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Resolution Path</CardTitle>
              <CardDescription>How the rate was determined</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {result.resolutionPath.map((step, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-xs font-medium">
                      {index + 1}
                    </div>
                    <div className="flex-1 text-sm">
                      {step.includes('FOUND') ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-green-700">{step}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">{step}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {result.baseRate && (
            <Card>
              <CardHeader>
                <CardTitle>Base Rate (for comparison)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Billing Rate</div>
                    <div className="text-lg font-semibold">
                      {formatCurrency(result.baseRate.billingRate || 0, result.baseRate.currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Cost Rate</div>
                    <div className="text-lg font-semibold">
                      {formatCurrency(result.baseRate.costRate || 0, result.baseRate.currency)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-900">Rate Resolution Priority</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge className="bg-purple-500">1</Badge>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Employee + Project Override (highest priority)</span>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-500">2</Badge>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Employee Global Override</span>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-green-500">3</Badge>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Project + Role Override</span>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="secondary">4</Badge>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Base Role Rate (lowest priority)</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
